import type {
  MatchEventCategory,
  MatchEventFrame,
  MatchEventsFilter,
  OpFilterMode
} from '../types/matchEvents.types';
import {
  DEFAULT_MATCH_EVENT_CATEGORIES,
  SPORT_IDS
} from '../types/matchEvents.types';
import {
  getCategoryDisplay,
  type CategoryDisplayConfig
} from '../components/matchEvents/category-display';

/**
 * Hard ceiling on how many raw frames the tab keeps in memory for one
 * fixture. The feed is a live, top-anchored view; without a cap the
 * buffer would grow unbounded over a 90-minute match. Older frames fall
 * off the bottom (a reconnect's `lastStreamId` replay re-seeds from the
 * cursor, and the server trims the stream anyway).
 */
export const MAX_MATCH_EVENT_ROWS = 5000;

/**
 * Parse a backend `match_state` `ts` (ISO-8601 UTC) to epoch millis for
 * ORDERING. A missing / unparseable `ts` sorts LOWEST (`-Infinity`) so it can
 * never shadow a value that carries a real timestamp. Never compare two `ts`
 * strings with lexicographic `<` — only this numeric form is format-safe.
 */
export function parseTsToMillis(ts: string | null | undefined): number {
  if (!ts) return -Infinity;
  const ms = Date.parse(ts);
  return Number.isNaN(ms) ? -Infinity : ms;
}

/**
 * Pick the FRESHER of the live WS `match_state` and the REST backfill by `ts`
 * (parsed numerically). Prevents a stale live value from permanently shadowing a
 * fresher REST snapshot recovered after a WS outage: whichever `{ state, ts }`
 * has the newer `ts` wins; ties keep the live value (the live channel is the
 * steady-state authority). Either side may be absent.
 */
export function pickFresherState<S>(
  live: { state: S; ts?: string | null } | null | undefined,
  backfill: { state: S; ts?: string | null } | null | undefined
): S | null {
  if (!live) return backfill?.state ?? null;
  if (!backfill) return live.state;
  return parseTsToMillis(backfill.ts) > parseTsToMillis(live.ts)
    ? backfill.state
    : live.state;
}

/**
 * Merge the durable history (persisted `match_events`, one per incident) with
 * the live in-memory buffer, de-duping by `streamId` (unique per stream entry;
 * falls back to `incidentKey:receivedAt`). History comes first so a refresh
 * rebuilds the full match feed; live updates layer on top. `buildMatchEventRows`
 * then collapses / sorts as usual.
 */
export function mergeHistoryAndLive(
  history: MatchEventFrame[],
  live: MatchEventFrame[]
): MatchEventFrame[] {
  const seen = new Set<string>();
  const out: MatchEventFrame[] = [];
  for (const f of [...history, ...live]) {
    // Keyless frames (no streamId AND no incidentKey) can't be identified, so
    // never de-dupe them — a shared receivedAt millisecond must not collapse two
    // genuinely distinct incidents.
    const key =
      f.streamId || (f.incidentKey ? `${f.incidentKey}:${f.receivedAt}` : '');
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(f);
  }
  return out;
}

/**
 * A goal LSports has explicitly annulled (VAR disallow). The authoritative
 * signal is a `Score` incident (category `goal`) whose raw `Body.Status` is
 * `"Cancelled"` — NOT a score revert. On the wire the cancelled `Score` keeps
 * the disallowed scoreline (e.g. still `"1:0"`, it does NOT drop back to
 * `"0:0"`), so a value-revert heuristic misses it entirely; only `Status`
 * carries the truth.
 */
export function isAnnulledGoal(frame: MatchEventFrame): boolean {
  if (frame.category !== 'goal') return false;
  const status = (frame.raw as { Status?: unknown } | null)?.Status;
  return status === 'Cancelled';
}

/**
 * The side (home/away) of the most recent still-standing goal annulment, or
 * null when none applies. Derived purely from the explicit `Status:"Cancelled"`
 * signal ([[isAnnulledGoal]]): the latest annulled goal wins, but a LEGIT goal
 * scored AFTER it clears the flag (the annulment is stale news once play moves
 * on). The annulled goal's own pre-cancellation frames arrive *before* the
 * cancellation, so they never clear it.
 */
export function deriveAnnulledSide(
  frames: MatchEventFrame[]
): 'home' | 'away' | null {
  let latest: MatchEventFrame | null = null;
  for (const f of frames) {
    if (!isAnnulledGoal(f)) continue;
    if (
      !latest ||
      new Date(f.receivedAt).getTime() >= new Date(latest.receivedAt).getTime()
    ) {
      latest = f;
    }
  }
  if (!latest) return null;
  const cancelAt = new Date(latest.receivedAt).getTime();
  for (const f of frames) {
    if (f.category !== 'goal' || isAnnulledGoal(f)) continue;
    if (new Date(f.receivedAt).getTime() > cancelAt) return null;
  }
  return latest.team;
}

/** A synthetic "end of period" divider inserted into the feed. */
export interface PeriodMarker {
  key: string;
  /** e.g. "End of 1st Half". */
  label: string;
  /** receivedAt of the first frame of the NEXT period (the transition point). */
  receivedAt: string;
}

/**
 * Synthesise an "end of &lt;period&gt;" marker at each forward period transition,
 * derived from the `period` every frame carries — no explicit LSports
 * "period ended" incident is needed. The period only ADVANCES (max id
 * increases), so late/out-of-order frames from an earlier period never
 * produce a spurious marker.
 */
export function derivePeriodMarkers(frames: MatchEventFrame[]): PeriodMarker[] {
  const markers: PeriodMarker[] = [];
  const seen = new Set<string>();
  for (const f of frames) {
    // LSports' EXPLICIT period signal: a "Period" incident (Kind "Period",
    // Id 2277) carries Status "Started" | "Finished" + the period name. We
    // surface the FINISHED transitions — end of 1st half, end of 2nd half
    // (= full time), end of a quarter, etc. This is the authoritative signal;
    // the old approach inferred it from the `period` field advancing across
    // stat frames, a proxy that mislabelled / missed transitions.
    if (f.kind !== 'Period') continue;
    const status = (f.raw as { Status?: unknown } | null)?.Status;
    if (status !== 'Finished') continue;
    const periodName = f.period?.name ?? 'period';
    const key = `period-end-${f.period?.id ?? periodName}`;
    if (seen.has(key)) continue; // one marker per finished period
    seen.add(key);
    markers.push({
      key,
      label: `End of ${periodName}`,
      receivedAt: f.receivedAt
    });
  }
  return markers;
}

/**
 * Game clock seconds → `mm'ss` (e.g. 125 → `2'05`, 5 → `0'05`). Null /
 * negative → `—`. The apostrophe form mirrors how live-score tickers
 * render match time.
 */
export function secondsToClock(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}'${String(secs).padStart(2, '0')}`;
}

/** Confidence 0..1 → integer percent (clamped 0..100). */
export function confidencePct(confidence: number | null | undefined): number {
  if (confidence == null || !Number.isFinite(confidence)) return 0;
  return Math.max(0, Math.min(100, Math.round(confidence * 100)));
}

/**
 * The home:away value string for the value column. Prefers the split
 * `homeValue`/`awayValue` positions; falls back to the single `value`.
 */
export function formatEventValue(frame: MatchEventFrame): string | null {
  if (frame.homeValue != null || frame.awayValue != null) {
    return `${frame.homeValue ?? '—'}:${frame.awayValue ?? '—'}`;
  }
  return frame.value;
}

/** One half's home:away reading for a metric. */
export interface HalfTally {
  periodId: number;
  periodName: string;
  home: string;
  away: string;
}

/**
 * A metric (goals / corners) broken down by half PLUS the match total.
 * LSports resets the accumulated count PER HALF, so the match total is the sum
 * of each half's latest reading — showing the current-half value alone would
 * under-count in the 2nd half.
 */
export interface MetricSummary {
  byHalf: HalfTally[];
  total: { home: number; away: number };
}

/** The match scoreboard derived from the live frames. */
export interface MatchSummary {
  goals: MetricSummary | null;
  corners: MetricSummary | null;
}

/**
 * Derive the goals + corners tally per HALF and the match total. For each
 * (category, period) we keep the highest-confidence reading (ties → the latest
 * in arrival order — follows increments + data corrections, ignores low-confidence
 * in-progress estimates). While in-play LSports accumulates per half (not across
 * the match), so the total is the SUM of the halves. But once a whole-match
 * summary period (FT/AET — [[isFullMatchSummaryPeriod]]) is posted, THAT reading
 * IS the match total (it is the cumulative full-match figure, not another half),
 * and the halves stay only as the breakdown — summing FT on top would double-count.
 */
/**
 * Period names LSports emits for the WHOLE-MATCH cumulative summary (posted once
 * the match ends / stage completes) — e.g. `FT` (full time), `AET` (after extra
 * time). Their reading is the authoritative match TOTAL, NOT another half that
 * accumulates independently, so they must never be summed on top of the halves
 * (that double-counts: 2H 1–0 + FT 1–0 → a bogus 2–0). Matched case-insensitively.
 */
const FULL_MATCH_SUMMARY_PERIODS = new Set([
  'ft',
  'full time',
  'aet',
  'after extra time',
  'ended'
]);

/** True when a period name is a whole-match cumulative summary (FT/AET/…). */
export function isFullMatchSummaryPeriod(
  name: string | null | undefined
): boolean {
  return (
    name != null && FULL_MATCH_SUMMARY_PERIODS.has(name.trim().toLowerCase())
  );
}

export function deriveMatchSummary(frames: MatchEventFrame[]): MatchSummary {
  // Per (category, period) we keep the highest-confidence reading (ties → the
  // latest in arrival order). A `Status:"Cancelled"` Score is NOT a VAR
  // annulment — per the ENGAGE docs it is a generic data-correction: the
  // overturned value is superseded by the corrected value re-emitted right
  // after, which this highest-confidence/latest selection already absorbs.
  const build = (category: MatchEventCategory): MetricSummary | null => {
    const bestPerPeriod = new Map<number, MatchEventFrame>();
    for (const f of frames) {
      if (f.category !== category) continue;
      if (f.homeValue == null || f.awayValue == null || f.period == null)
        continue;
      const prev = bestPerPeriod.get(f.period.id);
      if (!prev || f.confidence >= prev.confidence)
        bestPerPeriod.set(f.period.id, f);
    }
    if (bestPerPeriod.size === 0) return null;

    // Split the whole-match summary period(s) (FT/AET — cumulative total) from
    // the play halves (which accumulate independently and are summed).
    const summaryFrames = [...bestPerPeriod.values()].filter(f =>
      isFullMatchSummaryPeriod(f.period?.name)
    );
    const halfFrames = [...bestPerPeriod.values()].filter(
      f => !isFullMatchSummaryPeriod(f.period?.name)
    );

    const byHalf: HalfTally[] = halfFrames
      .sort(
        (a, b) =>
          (a.period as { id: number }).id - (b.period as { id: number }).id
      )
      .map(f => ({
        periodId: (f.period as { id: number }).id,
        periodName: (f.period as { name: string }).name,
        home: f.homeValue as string,
        away: f.awayValue as string
      }));

    // When LSports has posted a full-match summary (FT), THAT is the match total
    // — the halves are only the per-half breakdown. Otherwise (in-play) sum the
    // halves, since LSports accumulates per half, not across the match.
    const summary = summaryFrames.sort(
      (a, b) =>
        (b.period as { id: number }).id - (a.period as { id: number }).id
    )[0];
    const total = summary
      ? {
          home: Number(summary.homeValue) || 0,
          away: Number(summary.awayValue) || 0
        }
      : byHalf.reduce(
          (acc, h) => ({
            home: acc.home + (Number(h.home) || 0),
            away: acc.away + (Number(h.away) || 0)
          }),
          { home: 0, away: 0 }
        );
    return { byHalf, total };
  };
  return { goals: build('goal'), corners: build('corner') };
}

/**
 * Baseball scoreboard derived from the feed. Unlike soccer, the score is a
 * RUNNING total: LSports sends the cumulative home:away runs in each `run`
 * (Score) frame, so we take the latest authoritative reading directly — never
 * a per-inning sum.
 */
export interface BaseballSummary {
  /** Cumulative runs, home:away — the latest authoritative `run` frame. */
  runs: { home: number; away: number } | null;
  /** Cumulative home runs, home:away — the latest authoritative `home_run` frame. */
  homeRuns: { home: number; away: number } | null;
  /** Current inning name from the latest `inning` frame. */
  inning: string | null;
  /** A `final` (game-over) frame has been seen. */
  final: boolean;
}

/**
 * The highest-confidence frame of a category that carries a home:away reading;
 * ties break to the later `receivedAt` (freshest authoritative snapshot).
 */
function bestHomeAwayFrame(
  frames: MatchEventFrame[],
  category: MatchEventCategory
): MatchEventFrame | null {
  let best: MatchEventFrame | null = null;
  for (const f of frames) {
    if (f.category !== category) continue;
    if (f.homeValue == null || f.awayValue == null) continue;
    if (
      !best ||
      f.confidence > best.confidence ||
      (f.confidence === best.confidence &&
        new Date(f.receivedAt).getTime() >= new Date(best.receivedAt).getTime())
    ) {
      best = f;
    }
  }
  return best;
}

export function deriveBaseballSummary(
  frames: MatchEventFrame[]
): BaseballSummary {
  const runFrame = bestHomeAwayFrame(frames, 'run');
  const hrFrame = bestHomeAwayFrame(frames, 'home_run');

  let inning: string | null = null;
  let inningAt = -Infinity;
  for (const f of frames) {
    if (f.category !== 'inning') continue;
    const t = new Date(f.receivedAt).getTime();
    if (t >= inningAt) {
      inningAt = t;
      inning = f.period?.name ?? inning;
    }
  }

  const toPair = (
    f: MatchEventFrame | null
  ): { home: number; away: number } | null =>
    f
      ? { home: Number(f.homeValue) || 0, away: Number(f.awayValue) || 0 }
      : null;

  return {
    runs: toPair(runFrame),
    homeRuns: toPair(hrFrame),
    inning,
    final: frames.some(f => f.category === 'final')
  };
}

/** True when a summary is the baseball shape (discriminates the union). */
export function isBaseballSummary(
  summary: MatchSummary | BaseballSummary | TennisSummary
): summary is BaseballSummary {
  return 'runs' in summary;
}

/**
 * Tennis scoreboard derived from the feed. The `27 Score` `Values` layout
 * (sets vs games vs points) is NOT documented, so v1 shows the latest
 * authoritative `game` frame's RAW home:away reading verbatim — never parsed
 * or summed apart — plus the current set name and a game-over flag.
 */
export interface TennisSummary {
  /** Raw home:away from the latest authoritative `game` frame (NOT parsed). */
  game: { home: string; away: string } | null;
  /** Current set name from the latest `set` frame (`period.name`). */
  set: string | null;
  /** A `final` (match-over) frame has been seen. */
  final: boolean;
}

export function deriveTennisSummary(frames: MatchEventFrame[]): TennisSummary {
  const gameFrame = bestHomeAwayFrame(frames, 'game');

  let set: string | null = null;
  let setAt = -Infinity;
  for (const f of frames) {
    if (f.category !== 'set') continue;
    const t = new Date(f.receivedAt).getTime();
    if (t >= setAt) {
      setAt = t;
      set = f.period?.name ?? set;
    }
  }

  return {
    game: gameFrame
      ? {
          home: gameFrame.homeValue as string,
          away: gameFrame.awayValue as string
        }
      : null,
    set,
    final: frames.some(f => f.category === 'final')
  };
}

/** True when a summary is the tennis shape (discriminates the union). */
export function isTennisSummary(
  summary: MatchSummary | BaseballSummary | TennisSummary
): summary is TennisSummary {
  return 'game' in summary;
}

/**
 * Sport-aware scoreboard selector. Baseball (154914) → the running-total
 * {@link BaseballSummary}; tennis (54094) → the raw-game {@link TennisSummary};
 * every other / unknown sport → the soccer goals-and-corners
 * {@link deriveMatchSummary} (behaviour unchanged).
 */
export function deriveSummary(
  frames: MatchEventFrame[],
  sportId: number | null | undefined
): MatchSummary | BaseballSummary | TennisSummary {
  if (sportId === SPORT_IDS.BASEBALL) return deriveBaseballSummary(frames);
  if (sportId === SPORT_IDS.TENNIS) return deriveTennisSummary(frames);
  return deriveMatchSummary(frames);
}

/**
 * A flattened, display-ready view model for one match-event row. Pure
 * derivation from a `MatchEventFrame` — the component renders it and
 * owns nothing but layout. Keeping this pure makes the seconds→clock,
 * category→display and value collapse trivially unit-testable.
 */
export interface MatchEventRow {
  frame: MatchEventFrame;
  display: CategoryDisplayConfig;
  category: MatchEventCategory;
  name: string;
  team: 'home' | 'away' | null;
  playerName: string | null;
  value: string | null;
  clock: string;
  periodName: string | null;
  confidencePct: number;
  receivedAt: string;
  /** Stable dedupe/collapse key (incidentKey, falling back to streamId). */
  collapseKey: string;
}

/** Build the display-ready row view model from a raw frame. */
export function frameToRow(frame: MatchEventFrame): MatchEventRow {
  return {
    frame,
    display: getCategoryDisplay(frame.category),
    category: frame.category,
    name: frame.name,
    team: frame.team,
    playerName: frame.player?.name ?? null,
    value: formatEventValue(frame),
    clock: secondsToClock(frame.seconds),
    periodName: frame.period?.name ?? null,
    confidencePct: confidencePct(frame.confidence),
    receivedAt: frame.receivedAt,
    collapseKey: frame.incidentKey || frame.streamId || ''
  };
}

/** Newest-first by `receivedAt` (ISO). Ties keep input order (stable). */
function compareReceivedDesc(a: MatchEventFrame, b: MatchEventFrame): number {
  return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
}

/**
 * Collapse a list of frames down to ONE per `incidentKey`, keeping the
 * highest-confidence frame for each key (ties broken by the later
 * `receivedAt` — the freshest snapshot of that incident). Frames with an
 * empty `incidentKey` are never collapsed (each is treated as unique via
 * its `streamId`). The result is sorted newest-first by `receivedAt`.
 */
export function collapseByIncidentKey(
  frames: MatchEventFrame[]
): MatchEventFrame[] {
  const best = new Map<string, MatchEventFrame>();
  const uncollapsed: MatchEventFrame[] = [];

  for (const frame of frames) {
    const key = frame.incidentKey;
    if (!key) {
      uncollapsed.push(frame);
      continue;
    }
    const prev = best.get(key);
    if (
      !prev ||
      frame.confidence > prev.confidence ||
      (frame.confidence === prev.confidence &&
        new Date(frame.receivedAt).getTime() >=
          new Date(prev.receivedAt).getTime())
    ) {
      best.set(key, frame);
    }
  }

  return [...best.values(), ...uncollapsed].sort(compareReceivedDesc);
}

/**
 * Apply the tab's filter to the raw frame buffer and produce the ordered
 * list of display rows.
 *   1. category filter (empty selection ⇒ show nothing),
 *   2. min-confidence gate,
 *   3. collapse-by-incidentKey unless `showAll`,
 *   4. newest-first order + row view models.
 */
export function buildMatchEventRows(
  frames: MatchEventFrame[],
  filter: Pick<MatchEventsFilter, 'categories' | 'minConfidence' | 'showAll'>
): MatchEventRow[] {
  const selected = new Set(filter.categories);
  const filtered = frames.filter(
    f => selected.has(f.category) && f.confidence >= filter.minConfidence
  );
  const collapsed = filter.showAll
    ? [...filtered].sort(compareReceivedDesc)
    : collapseByIncidentKey(filtered);
  return collapsed.map(frameToRow);
}

// ─── URL-param persistence (mirrors NotificationsPage filter-in-URL) ────────
//
// Prefixed `ev*` keys so the match-events filter never collides with the
// notifications filter keys sharing the same query string.

export function readMatchEventsFilterFromParams(
  params: URLSearchParams
): MatchEventsFilter {
  const cats = params.getAll('evCat') as MatchEventCategory[];
  const confRaw = Number(params.get('evConf'));
  const fixtureRaw = params.get('evFixture');
  const fixtureId =
    fixtureRaw != null && fixtureRaw !== '' ? Number(fixtureRaw) : NaN;
  const opRaw = params.get('evOp');
  const opMode: OpFilterMode =
    opRaw === 'auto' || opRaw === 'manual' ? opRaw : 'all';
  return {
    categories: cats.length > 0 ? cats : [...DEFAULT_MATCH_EVENT_CATEGORIES],
    minConfidence:
      Number.isFinite(confRaw) && confRaw >= 0 && confRaw <= 1 ? confRaw : 0,
    showAll: params.get('evAll') === '1',
    raw: params.get('evRaw') === '1',
    fixtureId: Number.isFinite(fixtureId) ? fixtureId : null,
    opMode
  };
}

/**
 * Serialise ONLY the `ev*` keys for the match-events filter into the
 * given (mutated) params object. Every `ev*` key is cleared first so a
 * de-selection is reflected, and non-`ev*` keys (e.g. `tab`) are left
 * untouched so the caller can merge into the live search params.
 */
export function writeMatchEventsFilterToParams(
  filter: MatchEventsFilter,
  params: URLSearchParams
): URLSearchParams {
  for (const key of [
    'evCat',
    'evConf',
    'evAll',
    'evRaw',
    'evFixture',
    'evOp'
  ]) {
    params.delete(key);
  }
  for (const c of filter.categories) params.append('evCat', c);
  if (filter.minConfidence > 0)
    params.set('evConf', String(filter.minConfidence));
  if (filter.showAll) params.set('evAll', '1');
  if (filter.raw) params.set('evRaw', '1');
  if (filter.fixtureId != null)
    params.set('evFixture', String(filter.fixtureId));
  if (filter.opMode !== 'all') params.set('evOp', filter.opMode);
  return params;
}
